import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { messagesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './Messages.css';

export default function Messages() {
  const { uuid } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (uuid && conversations.length > 0) {
      const conv = conversations.find(c => c.uuid === uuid);
      if (conv) openConversation(conv);
    }
  }, [uuid, conversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await messagesAPI.getConversations();
      setConversations(res.data.data || []);
    } catch { toast.error('فشل تحميل المحادثات'); }
  };

  const openConversation = async (conv) => {
    setActiveConv(conv);
    try {
      const res = await messagesAPI.getMessages(conv.uuid);
      setMessages(res.data.data || []);
    } catch { toast.error('فشل تحميل الرسائل'); }
  };

  const handleSend = async () => {
    if (!text.trim() || !activeConv) return;
    setSending(true);
    try {
      const res = await messagesAPI.send(activeConv.uuid, { content: text });
      setMessages(prev => [...prev, res.data.data]);
      setText('');
    } catch { toast.error('فشل إرسال الرسالة'); }
    finally { setSending(false); }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="messages-page">
      <div className="messages-sidebar">
        <div className="messages-header">
          <h2>المحادثات</h2>
        </div>
        <div className="conversations-list">
          {conversations.length === 0 && (
            <div className="no-convs">لا توجد محادثات بعد</div>
          )}
          {conversations.map(conv => (
            <div
              key={conv.uuid}
              className={`conv-item ${activeConv?.uuid === conv.uuid ? 'active' : ''}`}
              onClick={() => openConversation(conv)}
            >
              <div className="conv-avatar-wrap">
                {conv.other_avatar ? (
                  <img src={`${process.env.REACT_APP_API_URL}/uploads/${conv.other_avatar}`} alt="" className="conv-avatar" />
                ) : (
                  <div className="conv-avatar-placeholder">{conv.other_name?.[0]}</div>
                )}
                {conv.unread_count > 0 && <span className="unread-badge">{conv.unread_count}</span>}
              </div>
              <div className="conv-info">
                <div className="conv-name-row">
                  <span className="conv-name">{conv.other_name}</span>
                  <span className="conv-time">{new Date(conv.last_message_at).toLocaleDateString('ar')}</span>
                </div>
                <p className="conv-preview">{conv.last_message || '...'}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="messages-main">
        {!activeConv ? (
          <div className="no-chat-selected">
            <span className="chat-icon">💬</span>
            <h3>اختر محادثة للبدء</h3>
            <p>تواصل مع المحترفين والمؤسسات الرياضية</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="chat-user-info">
                {activeConv.other_avatar ? (
                  <img src={`${process.env.REACT_APP_API_URL}/uploads/${activeConv.other_avatar}`} alt="" className="chat-header-avatar" />
                ) : (
                  <div className="chat-header-avatar placeholder">{activeConv.other_name?.[0]}</div>
                )}
                <div>
                  <h3>{activeConv.other_name}</h3>
                  <span className="online-status">متصل</span>
                </div>
              </div>
            </div>

            <div className="messages-list">
              {messages.map(msg => (
                <div key={msg.id} className={`message ${msg.sender_uuid === user?.uuid ? 'sent' : 'received'}`}>
                  <div className="message-bubble">{msg.content}</div>
                  <span className="message-time">
                    {new Date(msg.created_at).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input-area">
              <textarea
                className="message-input"
                placeholder="اكتب رسالتك..."
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                rows="1"
              />
              <button className="send-btn" onClick={handleSend} disabled={sending || !text.trim()}>
                {sending ? '...' : '←'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
